import type { ReactNode, RefObject } from "react"
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	MeasuringStrategy,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import {
	restrictToFirstScrollableAncestor,
	restrictToParentElement,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers"
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { IconDescendingSortingOutline18 as SortIcon } from "nucleo-ui-outline-18"

import type { RankUpdate } from "@productlane/lib/src/roadmap-custom-order"
import { mutators } from "@productlane/zero/mutators"

import type { RoadmapDragData } from "./reorder"
import type {
	CollisionDetection,
	DragEndEvent,
	DragStartEvent,
} from "@dnd-kit/core"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { reportServerRejection, useZero } from "@/lib/zero"
import { createClickGuard } from "./click-guard"
import {
	dragScope,
	GRIP_ATTRIBUTE,
	planGroupDrop,
	planRowDrop,
	sortableGroupId,
} from "./reorder"

export interface ReorderRow {
	id: string
	portalSortOrder?: number | null
}

// Groups collapse when one starts moving, so rects measured at drag start go
// stale; measuring every frame keeps the drop targets under the pointer.
const MEASURING = { droppable: { strategy: MeasuringStrategy.Always } }
// We pin the held header ourselves; dnd-kit's own scroll fix would fight it.
const AUTO_SCROLL = { layoutShiftCompensation: false }
// A row stays in its list. A group's header is the node, and its parent is its
// own collapsed group, so groups are kept to the scroll area instead.
const ROW_MODIFIERS = [restrictToVerticalAxis, restrictToParentElement]
const GROUP_MODIFIERS = [
	restrictToVerticalAxis,
	restrictToFirstScrollableAncestor,
]

const dragData = (data: unknown) => data as RoadmapDragData | undefined

const sameScopeOnly: CollisionDetection = (args) => {
	const scope = dragScope(dragData(args.active.data.current))
	return closestCenter({
		...args,
		droppableContainers: args.droppableContainers.filter(
			(container) => dragScope(dragData(container.data.current)) === scope,
		),
	})
}

interface Pin {
	id: string
	top: number
}

const headerOf = (list: HTMLElement, id: string) =>
	list.querySelector(
		`[data-reorder-id="${window.CSS.escape(id)}"] [${GRIP_ATTRIBUTE}]`,
	)?.parentElement ?? null

// Collapsing every group moves the held header away from the cursor; shift the
// list back before paint so the header stays where it was grabbed.
function HoldGroupInPlace({
	active,
	listRef,
	pinRef,
}: {
	active: RoadmapDragData | null
	listRef: RefObject<HTMLDivElement | null>
	pinRef: RefObject<Pin | null>
}) {
	useLayoutEffect(() => {
		const list = listRef.current
		if (!list) return
		const pin = pinRef.current
		list.style.transform = ""
		if (active?.kind !== "group" || !pin) {
			list.style.minHeight = ""
			return
		}
		const header = headerOf(list, pin.id)
		if (!header) return
		const shift = pin.top - header.getBoundingClientRect().top
		if (shift) list.style.transform = `translate3d(0, ${shift}px, 0)`
	}, [active, listRef, pinRef])
	return null
}

export function RoadmapReorderDnd({
	enabled,
	workspaceId,
	groups,
	storedGroupOrder,
	rowsByListId,
	onRowsReordered,
	onGroupsReordered,
	onReorderFailed,
	children,
}: {
	enabled: boolean
	workspaceId: string | undefined
	groups: readonly { key: string; label: string }[]
	storedGroupOrder: readonly string[]
	/** Every loaded row per list, which can be more than is rendered. */
	rowsByListId: ReadonlyMap<string, readonly ReorderRow[]>
	/** Called before the save, so the list shows the drop in the same frame. */
	onRowsReordered: (orders: RankUpdate[]) => void
	onGroupsReordered: (groupOrder: string[]) => void
	onReorderFailed: () => void
	children: (state: { groupDragActive: boolean }) => ReactNode
}) {
	const zero = useZero()
	const [active, setActive] = useState<RoadmapDragData | null>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const pinRef = useRef<Pin | null>(null)
	const [clickGuard] = useState(() => createClickGuard())
	useEffect(() => clickGuard.dispose, [clickGuard])

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const groupKeys = useMemo(() => groups.map((group) => group.key), [groups])
	// Labels, not keys: two headers can differ only by case and share a key.
	const groupIds = useMemo(
		() => groups.map((group) => sortableGroupId(group.label)),
		[groups],
	)

	const save = useCallback(
		(mutation: Parameters<typeof zero.mutate>[0]) => {
			reportServerRejection(zero.mutate(mutation), (message) => {
				onReorderFailed()
				toast.error({
					title: "Couldn't save the new order",
					description: message,
					icon: SortIcon,
				})
			})
		},
		[zero, onReorderFailed],
	)

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			clickGuard.arm()
			const data = dragData(event.active.data.current) ?? null
			const list = listRef.current
			pinRef.current = null
			if (data?.kind === "group" && list) {
				const id = String(event.active.id)
				const header = headerOf(list, id)
				if (header) {
					pinRef.current = { id, top: header.getBoundingClientRect().top }
					// Keeps the scroll position from clamping as the groups collapse.
					list.style.minHeight = `${list.offsetHeight}px`
				}
			}
			setActive(data)
		},
		[clickGuard],
	)

	const endDrag = useCallback(() => {
		clickGuard.release()
		pinRef.current = null
		setActive(null)
	}, [clickGuard])

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			endDrag()
			const from = dragData(event.active.data.current)
			const to = dragData(event.over?.data.current)
			if (!from || !to || dragScope(from) !== dragScope(to)) return

			if (from.kind === "group" && to.kind === "group") {
				const groupOrder = planGroupDrop(
					groupKeys,
					storedGroupOrder,
					from.groupKey,
					to.groupKey,
				)
				if (!groupOrder || !workspaceId) return
				onGroupsReordered(groupOrder)
				save(
					mutators.workspaces.updateRoadmapSettings({
						workspaceId,
						groupOrder,
					}),
				)
				analytics.track("roadmap-reorder-group")
				return
			}

			if (from.kind === "row" && to.kind === "row") {
				const orders = planRowDrop(
					rowsByListId.get(from.listId) ?? [],
					from.itemId,
					to.itemId,
				)
				if (orders.length === 0) return
				onRowsReordered(orders)
				save(
					from.rowType === "issue"
						? mutators.issues.setPortalSortOrders({ orders })
						: mutators.projects.setPortalSortOrders({ orders }),
				)
				analytics.track("roadmap-reorder-row", {
					type: from.rowType,
					writes: orders.length,
				})
			}
		},
		[
			endDrag,
			groupKeys,
			onGroupsReordered,
			onRowsReordered,
			rowsByListId,
			save,
			storedGroupOrder,
			workspaceId,
		],
	)

	if (!enabled) return <>{children({ groupDragActive: false })}</>

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={sameScopeOnly}
			measuring={MEASURING}
			autoScroll={AUTO_SCROLL}
			modifiers={active?.kind === "group" ? GROUP_MODIFIERS : ROW_MODIFIERS}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={endDrag}
		>
			<SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
				<div ref={listRef}>
					{children({ groupDragActive: active?.kind === "group" })}
				</div>
			</SortableContext>
			<HoldGroupInPlace active={active} listRef={listRef} pinRef={pinRef} />
		</DndContext>
	)
}
